/**
 * Pure calculation and validation logic for sales invoice drafts.
 *
 * All monetary calculations MUST go through core/calc computeDocument.
 * This module purely prepares DocumentInput and maps ComputedDocument results
 * for the UI without any side-effects, DOM, or database calls.
 */
import { computeDocument, type ComputedDocument, type DocumentInput } from '@core/calc';
import { resolvePlaceOfSupply } from '@core/gst/split';
import { asBasisPoints, asMoney, asQuantity, ZERO_QUANTITY } from '@core/money';
import { InvoiceServiceError } from '@data/services/invoiceService';
import type { Business, Party } from '@data/db/types';
import type { ProductListItem } from '@data/services/productReader';
import { financialYearFor } from '@domain/financialYear';

export interface DraftLineItem {
  readonly id: string;
  readonly productId: string;
  readonly quantityMilli: number;
  readonly ratePaise: number;
  readonly description?: string;
  readonly taxRateBp?: number;
}

export interface ComputeDraftTotalsInput {
  readonly business: Business;
  readonly customer: Party | null;
  readonly docDate: string;
  readonly lines: readonly DraftLineItem[];
  readonly productsById: ReadonlyMap<string, ProductListItem>;
  readonly roundOff?: boolean;
  /** State the goods are delivered to, when it differs from the customer address. */
  readonly deliveryStateCode?: string;
  /** An explicit place-of-supply override. Wins over every other source. */
  readonly placeOfSupplyOverride?: string;
}

/**
 * Pure calculation of invoice totals using the core engine.
 *
 * If lines are empty, returns null so the UI can display clean zeroes.
 */
export function computeInvoiceDraftTotals(
  input: ComputeDraftTotalsInput,
): ComputedDocument | null {
  if (input.lines.length === 0) {
    return null;
  }

  const fy = financialYearFor(input.docDate);

  // The place of supply is the BUSINESS state, not the customer's.
  //
  // This mirrors `issueSalesInvoice` exactly, and it has to: this function is the
  // preview of what that service will write. If the two disagreed, an interstate
  // customer would see IGST on screen and open a saved invoice carrying CGST +
  // SGST — the one class of bug this project exists to prevent (ADR 0003), just
  // moved from the print layer to the form.
  //
  // Consequence, stated plainly rather than hidden: interstate supply is charged
  // CGST + SGST until the service derives the place of supply from the customer.
  // That is a known gap in the WRITE path, not a display decision, and the
  // `matchesIssueSalesInvoice` test below fails loudly the day the service is
  // fixed without this following it.
  // OWNER DECISION: resolved from the transaction, NOT the business state.
  //
  // The SAME `resolvePlaceOfSupply` the write path calls, with the same inputs.
  // This is what makes the preview and the saved invoice agree: not a shared
  // constant, but the same function applied to the same transaction facts. If
  // this line said `input.business.stateCode` the screen would show one tax
  // split and the invoice would carry another, and both would reconcile.
  const resolvedPlaceOfSupply = resolvePlaceOfSupply({
    businessStateCode: input.business.stateCode,
    customerStateCode: input.customer?.stateCode ?? '',
    deliveryStateCode: input.deliveryStateCode ?? null,
    overrideStateCode: input.placeOfSupplyOverride ?? null,
  });

  // The locked decision: an UNRESOLVED place of supply must not silently become
  // the business state. computeDocument emits a
  // PLACE_OF_SUPPLY_UNRESOLVED warning for it, and the write path refuses to
  // issue; the preview shows that warning rather than a confident wrong number.
  const placeOfSupplyCode = resolvedPlaceOfSupply.resolved ? resolvedPlaceOfSupply.code : '';

  const docInput: DocumentInput = {
    business: {
      stateCode: input.business.stateCode,
      currency: 'INR',
    },
    docType: 'SALES_INVOICE',
    docDate: input.docDate,
    financialYear: fy,
    placeOfSupplyCode,
    reverseCharge: false,
    lines: input.lines.map((l, index) => {
      const prodItem = input.productsById.get(l.productId);
      // Use line's taxRateBp if provided (for line-level GST override), otherwise fall back to product's tax rate
      const taxRateBp = l.taxRateBp ?? prodItem?.taxRateBp ?? 0;
      return {
        id: l.id || `line-${index}`,
        productId: l.productId,
        description: l.description || prodItem?.product.name || 'Item',
        unit: prodItem?.product.unit || 'NOS',
        quantity: asQuantity(Math.max(0, Math.trunc(l.quantityMilli))),
        freeQuantity: ZERO_QUANTITY,
        ratePaise: asMoney(Math.max(0, Math.trunc(l.ratePaise))),
        discountType: 'NONE',
        discountValue: 0,
        taxRateBp: asBasisPoints(taxRateBp),
        cessRateBp: asBasisPoints(prodItem?.product.cessRateBp ?? 0),
      };
    }),
    charges: [],
    invoiceDiscount: null,
    roundOff: input.roundOff ?? false,
    roundOffMode: 'NEAREST_RUPEE',
    tcs: null,
  };

  return computeDocument(docInput);
}

/**
 * Map an error from invoice submission into a shopkeeper-readable message.
 */
export function explainInvoiceError(error: unknown): string {
  if (error instanceof InvoiceServiceError) {
    switch (error.code) {
      case 'NO_LINES':
        return 'Please add at least one line item before creating the invoice.';
      case 'NO_BUSINESS':
        return 'No active company is selected. Please select a company first.';
      case 'PARTY_NOT_FOUND':
        return 'The selected customer could not be found. Please choose an active customer.';
      case 'WRONG_BUSINESS':
        return error.message;
      case 'NO_ITEM':
        return 'One of the selected items could not be found in the catalogue.';
      default:
        return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Failed to create invoice. Please check the form and try again.';
}
